import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Shield, Zap, BarChart3 } from 'lucide-react';
import { Button } from './ui/button';
import { products } from '../mock';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../locales/translations';
import Header from './Header';
import Footer from './Footer';

const ProductDetail = () => {
  const { productSlug } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();

  const product = products.find((p) => p.slug === productSlug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [productSlug]);

  const scrollToProducts = () => {
    navigate('/', { state: { scrollTo: 'products' } });
  };

  if (!product) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Header lightBackground />
        <div className="min-h-screen flex items-center justify-center pt-20">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{t(language, 'productDetail.notFoundTitle')}</h1>
            <p className="text-gray-600 mb-8">{t(language, 'productDetail.notFound')}</p>
            <Button onClick={scrollToProducts} className="bg-[#996522] bg-[linear-gradient(56deg,_rgba(153,101,34,1)_0%,_rgba(255,180,41,1)_100%)] text-white">
              <ArrowLeft size={18} className="mr-2" />
              {t(language, 'serviceDetail.backHome')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const handleViewPdf = () => {
    if (product.pdfUrl) {
      const newWindow = window.open(product.pdfUrl, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.opener = null;
      }
    }
  };

  const scrollToContact = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-white pt-32">
      <Header lightBackground />
      {/* Back Button */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <button
          onClick={scrollToProducts}
          className="flex items-center text-orange-600 hover:text-orange-700 font-semibold transition-colors"
        >
          <ArrowLeft size={20} className="mr-2" />
          {t(language, 'serviceDetail.back')}
        </button>
      </div>

      {/* Hero Section - Image & Title */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center mb-16">
          {/* Left - Image */}
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-orange-600 to-red-600 rounded-3xl opacity-20 group-hover:opacity-40 transition duration-500 blur-xl"></div>
            <div className="relative bg-white rounded-3xl overflow-hidden shadow-2xl flex items-center justify-center min-h-[18rem] sm:min-h-[24rem] p-8 md:p-12">
              <img
                src={product.image}
                alt={product.name}
                className={`transition-transform duration-500 hover:scale-105 ${product.slug === 'skophi' || product.slug === 'desa-proaktif' || product.slug === 'zi-care'
                  ? 'w-auto h-auto max-w-[80%] max-h-[80%] md:max-w-[70%] md:max-h-[70%] object-contain object-center'
                  : 'w-full h-72 sm:h-96 object-cover'
                  }`}
              />
            </div>
          </div>

          {/* Right - Content */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 leading-tight">
                {product.name}
              </h1>
              <p className="text-base sm:text-xl text-gray-600 leading-relaxed">
                {typeof product.description === 'object'
                  ? product.description[language] || product.description.id
                  : product.description}
              </p>
            </div>

            {/* Key Features */}
            <div className="space-y-3 my-8">
              {(product.detailFeatures || product.features).map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3 group">
                  <div className="mt-1 p-1 bg-orange-100 group-hover:bg-orange-600 transition-colors rounded-full">
                    <Check size={16} className="text-orange-600 group-hover:text-white" />
                  </div>
                  <span className="text-gray-700 font-medium">{typeof feature === 'object' ? (feature[language] || feature.id) : feature}</span>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <Button
              onClick={handleViewPdf}
              disabled={!product.pdfUrl}
              className="w-full bg-[#996522] bg-[linear-gradient(56deg,_rgba(153,101,34,1)_0%,_rgba(255,180,41,1)_100%)] text-white hover:brightness-110 py-6 text-lg font-bold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:brightness-100"
            >
              {product.pdfUrl
                ? t(language, 'serviceDetail.downloadFullSpec')
                : t(language, 'serviceDetail.pdfNotAvailable')}
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-r from-orange-50 to-red-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {language === 'id' ? 'Keuntungan Utama' : 'Key Benefits'}
            </h2>
            <p className="text-lg text-gray-600">
              {language === 'id' ? 'Solusi lengkap untuk transformasi digital bisnis Anda' : 'Complete solutions for your business digital transformation'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: language === 'id' ? 'Keamanan' : 'Security', desc: language === 'id' ? 'Perlindungan data berlapis' : 'Multi-layer data protection' },
              { icon: Zap, title: language === 'id' ? 'Performa' : 'Performance', desc: language === 'id' ? 'Kecepatan dan efisiensi' : 'Speed and efficiency' },
              { icon: BarChart3, title: language === 'id' ? 'Skalabilitas' : 'Scalability', desc: language === 'id' ? 'Tumbuh bersama bisnis Anda' : 'Grow with your business' },
              { icon: Check, title: language === 'id' ? 'Reliabilitas' : 'Reliability', desc: language === 'id' ? 'Uptime 99.9% terjamin' : 'Guaranteed 99.9% uptime' },
            ].map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={idx}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-orange-100 hover:border-orange-400 group"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-red-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="text-orange-600" size={24} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Details */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {language === 'id' ? 'Fitur Komprehensif' : 'Comprehensive Features'}
            </h2>
            <p className="text-lg text-gray-600">
              {language === 'id' ? 'Semua yang Anda butuhkan dalam satu platform' : 'Everything you need in one platform'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {(product.detailFeatures || product.features).map((feature, idx) => (
              <div
                key={idx}
                className="relative group overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-orange-400"
              >
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-orange-400 to-red-400 rounded-full opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-3xl"></div>
                <div className="relative z-10">
                  <div className="flex items-start gap-4">
                    <div className="mt-1 p-2 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg">
                      <Check size={20} className="text-white" />
                    </div>
                    <p className="text-lg font-semibold text-gray-900 leading-relaxed">
                      {typeof feature === 'object' ? (feature[language] || feature.id) : feature}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-red-600 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            {language === 'id' ? 'Siap untuk Transformasi?' : 'Ready for Transformation?'}
          </h2>
          <p className="text-xl text-orange-50 mb-8 max-w-2xl mx-auto">
            {language === 'id'
              ? 'Konsultasikan kebutuhan bisnis Anda dengan tim ahli kami'
              : 'Consult your business needs with our expert team'
            }
          </p>
          <Button
            onClick={scrollToContact}
            className="bg-white text-orange-600 hover:bg-orange-50 px-8 py-6 text-lg font-bold rounded-xl transition-all duration-300 hover:scale-105"
          >
            {t(language, 'serviceDetail.freeConsultation')}
          </Button>
        </div>
      </section>

      {/* Related Products */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12">
            {language === 'id' ? 'Produk Terkait' : 'Related Products'}
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {products
              .filter(p => p.id !== product.id)
              .slice(0, 3)
              .map(relatedProduct => (
                <div
                  key={relatedProduct.id}
                  onClick={() => navigate(`/products/${relatedProduct.slug}`)}
                  className="bg-gradient-to-br from-gray-50 to-white rounded-3xl overflow-hidden shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 cursor-pointer group"
                >
                  <div className="relative h-40 overflow-hidden bg-white flex items-center justify-center">
                    <img
                      src={relatedProduct.image}
                      alt={relatedProduct.name}
                      className={`w-full h-full group-hover:scale-110 transition-transform duration-500 ${relatedProduct.slug === 'skophi' || relatedProduct.slug === 'desa-proaktif' || relatedProduct.slug === 'zi-care'
                        ? 'object-contain object-center'
                        : 'object-cover'
                        }`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent"></div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{relatedProduct.name}</h3>
                    <p className="text-gray-600 text-sm line-clamp-2">
                      {typeof relatedProduct.description === 'object'
                        ? relatedProduct.description[language] || relatedProduct.description.id
                        : relatedProduct.description}
                    </p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ProductDetail;


